import {
  Component,
  Element,
  Event,
  EventEmitter,
  FunctionalComponent,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import HLS from 'hls.js'
import { clamp, noop } from 'lodash-es'
import mux from 'mux-embed'
import playerjs from 'player.js'
import { dayjs } from 'src/lib/dayjs'
import { css } from 'src/lib/emotion'
import { gsap } from 'src/lib/gsap'
import { RenderFn } from 'src/types'

import { AppSharedPropsStore } from '../app'
import { formatClipDuration } from '../utils/clip'
import { classes } from '../utils/css'
import { logWithBadge } from '../utils/logging'
import { getStoredValue, setStoredValue } from '../utils/storage'

const Timestamp: FunctionalComponent<{
  currentTime: number
}> = ({ currentTime }) => {
  const displayTime = currentTime
  const format = currentTime >= 3600 ? 'H:mm:ss' : 'm:ss'
  return (
    <button class='flex items-end'>
      <span
        class='inline-block text-sm font-bold text-right'
        style={{ width: format.length + 'ch', fontFeatureSettings: '"tnum"' }}
      >
        {dayjs.duration(displayTime, 's').format(format)}
      </span>
    </button>
  )
}

const SpeedControl: FunctionalComponent<{
  currentSpeed: number
  speedOptions: number[]
  onSelect: (speed: number) => void
}> = ({ currentSpeed, speedOptions, onSelect }) => (
  <button
    class='flex items-end cursor-pointer'
    onClick={() => {
      const index = speedOptions.indexOf(currentSpeed) + 1
      onSelect(speedOptions[index] || speedOptions[0])
    }}
  >
    <span class='inline-block text-sm font-bold text-right' style={{ width: '2ch' }}>
      {currentSpeed}
    </span>
    <ui-icon icon='x' class='-ml-1px text-sm' />
    <ui-tooltip anchor='top' on='hover' bgColor='surface-low'>
      Playback Speed
    </ui-tooltip>
  </button>
)

const overlay = css`
  :before {
    content: '';
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 30%, transparent);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 40%;
  }
`

const DEFAULT_HLS_MAX_BUFFER_LENGTH = 30
@Component({
  tag: 'video-player',
})
export class VideoPlayer {
  @Element() el: HTMLVideoPlayerElement

  @Prop() src: string
  @Prop() startTime = 0
  @Prop({ mutable: true }) endTime = 0
  @Prop() currentTime = 0
  @Prop() autoplay = false
  @Prop() withCredentials = false
  @Prop() iconSize = '140px'
  @Prop() convertToPlayerTime = (time: number): number => time
  @Prop() convertFromPlayerTime = (time: number): number => time
  @Prop() trackingSessionId: string
  @Prop() muxVideoId: string
  @Prop() muxVideoTitle: string
  @Prop() muxPageType: string

  @Prop() renderOverlay = noop as RenderFn
  @Prop() renderActions = noop as RenderFn
  @Prop() renderSpans = noop as RenderFn
  @Prop() renderMarkers = noop as RenderFn
  @Prop() showGradients = true
  @Prop() alwaysHideControlsOnMouseLeave = false

  @Event() metadataLoaded: EventEmitter<HTMLVideoElement>
  @Event() timeUpdated: EventEmitter<HTMLVideoElement>
  @Event() paused: EventEmitter<HTMLVideoElement>
  @Event() played: EventEmitter<HTMLVideoElement>
  @Event() rateChanged: EventEmitter<HTMLVideoElement>
  @Event() videoSeeked: EventEmitter<HTMLVideoElement>
  @Event() videoEnded: EventEmitter<HTMLVideoElement>

  @State() videoSpeed = getStoredValue('callVideoSpeed', 1)
  @State() videoMuted = getStoredValue('callVideoMuted', false)
  @State() videoTime = 0
  @State() videoPaused = true
  @State() seeking: boolean
  @State() loaded = false
  @State() scrubbing: boolean
  @State() hideOverlay = true
  @State() unplayed = true

  playControlEl: HTMLElement
  scrubRect: DOMRect
  playheadEl: HTMLUiPlayheadElement
  hlsInstance: HLS
  reloadTime = 0

  playStartTime = 0
  playTotalTime = 0

  resumeAfterSeek: boolean
  hasEnded = false

  playPromise: Promise<void>

  get videoDuration() {
    /*
     * TODO:  figure out a better duration management for all possible player
     * inputs.  This is commented out because we're more likely to see a note
     * without trimming than an trimmed one, and the resulting difference in the
     * player progress bar and duration header is much more obvious on some
     * notes without trimming (up to 12 seconds extra).
    if (this.loaded) {
      // The loaded player duration is a more reliable source since for some
      // videos, like notes, since the start and end time may be relative to the
      // note but not consider trimmed portions.
      return this.videoEl.duration
    }
    */
    return this.convertToPlayerTime(this.endTime) - this.convertToPlayerTime(this.startTime)
  }

  get formattedVideoDuration() {
    return formatClipDuration(this.videoDuration / this.videoSpeed, false)
  }

  @Watch('videoSpeed')
  onSpeedChanged() {
    this.videoEl.playbackRate = this.videoSpeed
    setStoredValue('callVideoSpeed', this.videoSpeed)
    this.rateChanged.emit(this.videoEl)
  }

  @Watch('videoMuted')
  onMutedChanged() {
    setStoredValue('callVideoMuted', this.videoMuted)
  }

  // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted

  safePlay() {
    this.videoEl.muted = false
    return (this.playPromise = this.videoEl.play())
  }

  async safePause() {
    const promise = this.playPromise
    if (promise) {
      return promise.then(() => {
        if (promise === this.playPromise) {
          this.videoEl.pause()
        }
      })
    } else {
      this.videoEl.pause()
    }
  }

  @Method()
  async setCurrentTime(time: number) {
    const seek_position = this.convertToPlayerTime(time)
    this.videoEl.currentTime = seek_position
  }

  @Method()
  async setCurrentTimePercent(pct: number) {
    const seek_position = this.convertToPlayerTime(this.startTime) + pct * this.videoDuration
    this.videoEl.currentTime = seek_position
  }

  @Method()
  async play(time?: number) {
    // Compare to null so that 0 is allowed
    // Use != rather than !== so that if time is undefined, it is coerced to null
    /* eslint-disable eqeqeq */
    if (time != null) {
      await this.setCurrentTime(time)
    }
    /* eslint-enable eqeqeq */
    return this.safePlay()
  }

  @Method()
  async pause() {
    return this.safePause()
  }

  @Method()
  async reload(reloadTime: number) {
    logWithBadge('Player', 'reload', this.src)
    this.reloadTime = reloadTime
    this.onSetSrc()
  }

  videoEl: HTMLVideoElement
  videoSpeedOptions = [1, 1.1, 1.2, 1.3, 1.4, 1.5, 2]

  onVideoMetadata = () => {
    this.loaded = true
    this.videoEl.playbackRate = this.videoSpeed
    this.videoEl.volume = this.videoMuted ? 0 : 1
    this.endTime = this.endTime || this.videoEl.duration

    if (this.reloadTime) {
      this.setCurrentTime(this.reloadTime)
      if (this.videoPaused) {
        this.showControls()
        this.safePause()
      } else {
        this.hideControls()
        this.safePlay()
      }
    } else {
      this.setCurrentTime(this.currentTime || this.startTime)
      if (!this.autoplay) {
        this.pause()
      } else {
        this.videoEl.muted = false
      }
    }

    this.metadataLoaded.emit(this.videoEl)
  }

  onVideoUpdate = () => {
    this.videoTime = this.videoEl.currentTime - this.convertToPlayerTime(this.startTime)
    this.timeUpdated.emit(this.videoEl)
    if (this.videoEl.currentTime >= this.convertToPlayerTime(this.endTime)) {
      const wasPlaying = !this.videoPaused
      this.pause()
      if (wasPlaying) {
        // Do not emit the ended event if the user is scrolling while the video is paused
        this.onVideoEnded()
      }
      return
    }
  }

  onVideoPause = () => {
    this.showControls()
    this.videoPaused = true
    this.paused.emit(this.videoEl)
    this.playTotalTime += new Date().getTime() - this.playStartTime
  }

  onVideoPlay = () => {
    this.hideControls()
    this.videoPaused = false
    this.unplayed = false
    this.played.emit(this.videoEl)
    this.playStartTime = new Date().getTime()

    // If we're an hls instance, update the config to buffer more
    if (this.hlsInstance?.config && this.hlsInstance.config.maxBufferLength < DEFAULT_HLS_MAX_BUFFER_LENGTH) {
      this.hlsInstance.config.maxBufferLength = DEFAULT_HLS_MAX_BUFFER_LENGTH
    }
  }

  onVideoEnded = () => {
    this.setCurrentTime(this.startTime)
    if (!this.hasEnded) {
      this.hasEnded = true
      this.videoEnded.emit(this.videoEl)
    }
  }

  hideControls() {
    if (this.playControlEl) {
      gsap.to(this.playControlEl, { opacity: 0, duration: 0.2 })
    }
    this.hideOverlay = false
  }

  showControls() {
    if (this.playControlEl) {
      gsap.to(this.playControlEl, { opacity: 1, duration: 0.2 })
    }
    this.hideOverlay = true
  }

  startScrub() {
    this.resumeAfterSeek = !this.videoPaused
    this.scrubRect = this.playheadEl.getBoundingClientRect()
    this.scrubbing = true
    this.pause()
  }

  @Listen('mousemove', { target: 'window', passive: true })
  onScrub(e: MouseEvent) {
    if (this.scrubbing) {
      const pct = clamp((e.pageX - this.scrubRect.left) / this.scrubRect.width, 0, 1)
      this.playheadEl.position = pct
      this.setCurrentTimePercent(pct)
    }
  }

  @Listen('mouseup', { target: 'window' })
  endScrub() {
    if (!this.scrubbing) {
      return
    }
    this.scrubbing = false
    if (this.resumeAfterSeek) {
      this.safePlay()
    }
  }

  @Watch('src')
  onSetSrc() {
    this.loaded = false
    this.playControlEl = null
    const isHLS = this.src.split('?')[0].endsWith('.m3u8')
    if (isHLS && HLS.isSupported()) {
      if (this.hlsInstance) {
        this.hlsInstance.detachMedia()
        this.hlsInstance.destroy()
      }
      this.hlsInstance = new HLS({
        xhrSetup: (xhr) => (xhr.withCredentials = this.withCredentials), // do send cookies
        maxBufferLength: 1, // Only buffer the first fragment to start
      })
      this.hlsInstance.loadSource(this.src)
      this.hlsInstance.attachMedia(this.videoEl)
      mux.monitor(this.videoEl, {
        debug: false,
        hlsjs: this.hlsInstance,
        Hls: HLS,
        data: {
          env_key: AppSharedPropsStore.state.muxEnvKey,
          player_name: 'HLS.js',
          player_version: HLS.version,
          video_id: this.muxVideoId,
          video_title: this.muxVideoTitle,
          video_series: this.muxPageType,
          page_type: this.muxPageType,
        },
      })
    } else if (!isHLS || this.videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // eslint-disable-next-line
      this.videoEl.src = this.src
      mux.monitor(this.videoEl, {
        debug: false,
        data: {
          env_key: AppSharedPropsStore.state.muxEnvKey,
          video_id: this.muxVideoId,
          video_title: this.muxVideoTitle,
          video_series: this.muxPageType,
          page_type: this.muxPageType,
        },
      })
    }
  }

  @Watch('seeking')
  onSeeking(current, previous) {
    if (previous && !current) {
      this.videoSeeked.emit(this.videoEl)
    }
  }

  @Watch('currentTime')
  onCurrentTime(time: number) {
    this.play(time)
  }

  componentDidLoad() {
    if (this.src) {
      this.onSetSrc()
    }
    const adapter = playerjs.HTML5Adapter(this.videoEl)
    adapter.ready()
  }

  disconnectedCallback() {
    if (this.hlsInstance) {
      this.hlsInstance.detachMedia()
      this.hlsInstance.destroy()
      this.hlsInstance = null
    }
  }

  render() {
    return (
      <Host class='group relative'>
        <video
          autoplay
          playsinline
          preload='metadata'
          muted
          class='object-contain w-full h-full bg-black rounded-t-md'
          ref={(el) => (this.videoEl = el)}
          onLoadedMetaData={this.onVideoMetadata}
          onTimeUpdate={this.onVideoUpdate}
          onPause={this.onVideoPause}
          onPlay={this.onVideoPlay}
          onEnded={this.onVideoEnded}
          onSeeking={() => (this.seeking = true)}
          onSeeked={() => (this.seeking = false)}
        />
        <menu class='flex absolute inset-0 z-pin flex-col p-0 m-0'>
          <div
            ref={(el) => (this.playControlEl = el)}
            class={`
              text-white cursor-pointer absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-75 rounded-t-md
              ${classes(!this.loaded, 'pointer-events-none')}
            `}
            onClick={() => (this.videoPaused ? this.safePlay() : this.safePause())}
            onMouseEnter={() => {
              this.showControls()
            }}
            onMouseLeave={() => {
              if (this.alwaysHideControlsOnMouseLeave || !this.videoPaused) {
                this.hideControls()
              }
            }}
          >
            <div
              hidden={!this.loaded}
              class='flex relative flex-col items-center text-white text-opacity-25 hover:text-opacity-50'
            >
              <ui-icon icon={this.videoPaused ? 'play' : 'pause'} style={{ fontSize: this.iconSize }} />
              {this.unplayed && (
                <span class='absolute bottom-0 mr-4 text-base font-bold'>{this.formattedVideoDuration}</span>
              )}
            </div>
          </div>
          {!this.loaded && (
            <div class='flex absolute inset-0 justify-center items-center text-4xl'>
              <ui-spinner />
            </div>
          )}
          {this.renderOverlay && (
            <div class={`${classes(this.showGradients && !this.hideOverlay, overlay, 'z-menu')}`}>
              {this.renderOverlay()}
            </div>
          )}
          <section
            class={`
              absolute transition duration-200 pl-3 pt-2 pb-3 pr-4 bottom-0 left-0 right-0 flex items-center text-2xl text-white spacing-x-2
            `}
            style={
              this.showGradients && {
                backgroundImage: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8))',
              }
            }
          >
            <ui-icon
              class='z-menu cursor-pointer'
              onClick={() => {
                this.videoMuted = !this.videoMuted
                this.videoEl.volume = this.videoMuted ? 0 : 1
              }}
              icon={this.videoMuted ? 'volume-mute' : 'volume-up'}
            />
            <Timestamp currentTime={this.videoTime} />
            <ui-playhead
              ref={(el) => (this.playheadEl = el)}
              class='flex-auto h-2 bg-surface-highest bg-opacity-75 cursor-pointer'
              position={this.videoDuration ? this.videoTime / this.videoDuration : 0}
              allowClick
              onClicked={(e) => {
                this.playheadEl.position = e.detail
                this.setCurrentTimePercent(e.detail)
              }}
              renderMarkers={this.renderMarkers}
              renderSpans={this.renderSpans}
              renderPlayhead={() => (
                <button slot='playhead' class='w-16px' onMouseDown={() => this.startScrub()}>
                  <div class='m-auto mt-3px w-3px h-full bg-link shadow-contrast' style={{ height: '22px' }}></div>
                </button>
              )}
            />
            <SpeedControl
              speedOptions={this.videoSpeedOptions}
              currentSpeed={this.videoSpeed}
              onSelect={(speed) => (this.videoSpeed = speed)}
            />
            {this.renderActions()}
          </section>
        </menu>
      </Host>
    )
  }
}
